<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import QuantitySelector from './components/quantity-selector.vue';
import WorkOrderItems from './components/work-order-items.vue';
import AllocationEdition from './components/allocation-edition.vue';

export default {
  props: {
    itemId: { type: Number, required: true },
    repository: { type: Object, required: true },
  },
  components: {
    Loading,
    QuantitySelector,
    WorkOrderItems,
    AllocationEdition,
  },
  data() {
    return {
      isLoading: false,
      mode: 'CONSUME',
      workOrder: null,
      lastLoadingTime: null,
      item: null,
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      try {
        this.isLoading = true;
        this.workOrder = null;
        this.lastLoadingTime = Date.now().toString();

        let resp = await this.repository.customREST.getItem(this.itemId);
        this.item = resp.data;
      } catch (error) {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    async confirmationClicked(quantity) {
      try {
        this.isLoading = true;

        let payload = {
          quantity: quantity,
        };

        let resp = null;
        if (this.mode == 'CONSUME') {
          payload.workOrderId = this.workOrder?.id ?? 0;

          resp = await this.repository.actions.consume(this.itemId, payload);
        } else {
          resp = await this.repository.actions.add(this.itemId, payload);
        }

        this.$toast.successToast(this.$t('general.success'));

        this.item = resp.data;

        this.lastLoadingTime = Date.now().toString();
      } catch (error) {
        if (error.response != null && error.response.status == 409) {
          this.$toast.warningToast(this.$t('errors.wasDesyncedTryAgainBulk'));
        } else {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        }
      } finally {
        this.isLoading = false;
      }
    },
    workOrderSelected(workOrder) {
      this.workOrder = workOrder;
    },
  },
  computed: {
    maxConsumption() {
      if (this.item == null) return null;

      return this.toFloat(
        this.item.totalQuantity -
          this.item.reservedQuantity +
          this.workOrderItemsReserved
      );
    },
    currentInventory() {
      if (this.item == null) return 0;

      return this.toFloat(this.item.totalQuantity);
    },
    workOrderItemsReserved() {
      if (this.workOrder == null) return 0;

      if (this.workOrder.workOrderItems == null) return 0;

      return this.workOrder.workOrderItems.reduce(
        (n, { reservedQuantity }) => n + reservedQuantity,
        0
      );
    },
  },
  watch: {
    mode() {
      this.workOrder = null;
    },
    itemId() {
      this.loadData();
    },
  },
};
</script>

<template>
  <div>
    <loading
      :active="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="true"
    >
    </loading>
    <div v-if="item != null">
      <header>
        <b-button-group class="header-buttons" size="lg">
          <b-button
            class="header-button"
            :variant="mode == 'CONSUME' ? 'danger' : 'outline-danger'"
            @click="mode = 'CONSUME'"
          >
            {{ $t('items.consume') }}
          </b-button>
          <b-button
            class="header-button"
            :variant="mode == 'ADD' ? 'success' : 'outline-success'"
            @click="mode = 'ADD'"
          >
            {{ $t('items.add') }}
          </b-button>
        </b-button-group>
      </header>

      <h2 class="text-center">
        {{ item.name }}
      </h2>

      <main :key="lastLoadingTime">
        <AllocationEdition
          :allocationId="item.allocationId"
          :itemId="item.id"
          :repository="repository"
        />

        <WorkOrderItems
          v-if="mode == 'CONSUME'"
          :itemId="item.id"
          :mode="mode"
          @work-order-selected="workOrderSelected"
          :repository="repository"
        />

        <div style="height: 20px"></div>

        <QuantitySelector
          :mode="mode"
          :quantityType="item.quantityType"
          :maxConsumption="maxConsumption"
          :currentInventory="currentInventory"
          @confirmation-clicked="confirmationClicked"
        />
      </main>
    </div>
  </div>
</template>

<style>
.no_overflow .multiselect__content-wrapper {
  max-width: 100%;
  border-top: 1px solid #e8e8e8;
}

.multiselect--active .multiselect__tags {
  border-bottom: none;
}
</style>

<style>
html {
  background-color: white;
}

.page button {
  font-size: 1.6em !important;
  height: 60px;
}
</style>

<style scoped>
.page {
  padding: 10px;
  width: calc(100vw);
  background-color: white;
}

.header-buttons {
  width: 100%;
}

.header-button {
  width: 50%;
}

main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

h2 {
  margin-top: 10px;
  margin-bottom: 20px;
}
</style>